import { ChevronRightIcon, MagnifyingGlassIcon } from "@radix-ui/react-icons";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import clsx from "clsx";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ProjectTimeTrackingAnalyticsData,
  useGetProjectTimeTrackingAnalytics,
} from "src/api/Analytics/utilization";
import { ProjectsV2FilterGroup } from "src/api/Services/Projects/projects";
import { useGetTaskersForInstance } from "src/api/Services/Tasks/tasks";
import { useGetSpaces } from "src/api/Spaces/spaces";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "src/components/ui/hover-card";
import { Loading } from "src/components/ui/loading";
import { TextInput } from "src/components/ui/text-input";
import { formatCurrencyValue } from "src/pages/Services/Projects/NewProject/NewProject";
import { fuzzyFilter } from "src/pages/Services/Projects/ProjectsTable";
import { clean, Filter, StatusFilter } from "src/pages/spaces/space/projects";
import { getProjectFinancialsAnalyticsFilterKey } from "src/utils/filters";

type Props = {};

export default function ProjectFinancialsAnalytics({}: Props) {
  const [filterGroup, setFilterGroup] = useState<ProjectsV2FilterGroup>(() => {
    const savedFilterGroup = localStorage.getItem(
      getProjectFinancialsAnalyticsFilterKey()
    );
    if (savedFilterGroup) {
      return JSON.parse(savedFilterGroup);
    }
    return {
      logic: "AND",
      children: [
        {
          field: "project-status",
          comparator: "in",
          value: ["In Progress"],
        },
      ],
    };
  });
  useEffect(() => {
    if (filterGroup.children.length === 0) {
      localStorage.removeItem(getProjectFinancialsAnalyticsFilterKey());
    } else {
      localStorage.setItem(
        getProjectFinancialsAnalyticsFilterKey(),
        JSON.stringify(filterGroup)
      );
    }
  }, [filterGroup]);
  const { data: timeTrackingData } = useGetProjectTimeTrackingAnalytics(
    clean(filterGroup)
  );
  const { data: instanceMembers } = useGetTaskersForInstance();
  const { data: spaces } = useGetSpaces();
  const [search, setSearch] = useState("");
  return (
    <div className="flex h-full flex-col">
      <div className="flex h-12 items-center border-b border-gray-200/70 px-8 text-sm text-gray-800">
        <div className="flex items-center space-x-2 text-sm">
          <Link to={`/analytics`}>
            <div className="text-gray-800">{"Analytics"}</div>
          </Link>
          <ChevronRightIcon className="h-3 w-3 text-gray-500" />
          <div className="cursor-default text-gray-800">
            {"Project Financials"}
          </div>
        </div>
      </div>
      <div className="flex h-14 items-center gap-2 border-b border-gray-200/70 px-8">
        {/* <Filter
            filterGroup={filterGroup}
            setFilterGroup={setFilterGroup}
            isLoading={!projectOwners}
            // localStorageKey="project-filter"
            filterModels={[
              {
                field: "project-owner",
                projectOwners:
                  // Array.from(
                  //   new Map(
                  //     projects?.projects.map((p) => [
                  //       p.owner._id,
                  //       {
                  //         _id: p.owner._id,
                  //         name: p.owner.name,
                  //         email: p.owner.email,
                  //       },
                  //     ])
                  //   ).values()
                  // ) ?? [],
                  projectOwners?.map((m) => {
                    return {
                      _id: m._id,
                      name: m.name,
                      email: m.email,
                    };
                  }) ?? [],
              },
              { field: "project-status" },
              { field: "company" },
              {
                field: "project-template",
                projectTemplates:
                  journeyTemplates
                    ?.filter((jt) => jt.spaceId?.toString() === spaceId)
                    .map((jt) => {
                      return {
                        _id: jt._id?.toString() ?? "",
                        name: jt.name,
                      };
                    }) ?? [],
              },
            ]}
          /> */}
        <Filter
          filterGroup={filterGroup}
          setFilterGroup={setFilterGroup}
          isLoading={!instanceMembers}
          // localStorageKey="project-filter"
          filterModels={[
            {
              field: "project-owner",
              projectOwners:
                // Array.from(
                //   new Map(
                //     projects?.projects.map((p) => [
                //       p.owner._id,
                //       {
                //         _id: p.owner._id,
                //         name: p.owner.name,
                //         email: p.owner.email,
                //       },
                //     ])
                //   ).values()
                // ) ?? [],
                instanceMembers?.map((m) => {
                  return {
                    _id: m._id,
                    name: m.name,
                    email: m.email,
                  };
                }) ?? [],
            },
            // { field: "project-status" },
            { field: "company" },
            {
              field: "space",
              spaces:
                spaces
                  ?.filter((s) => s.settings.timeTracking)
                  .map((s) => ({
                    _id: s._id,
                    name: s.name,
                    color: s.color,
                  })) ?? [],
            },
            // {
            //   field: "project-template",
            //   projectTemplates:
            //     journeyTemplates
            //       ?.filter((jt) => jt.spaceId?.toString() === spaceId)
            //       .map((jt) => {
            //         return {
            //           _id: jt._id?.toString() ?? "",
            //           name: jt.name,
            //         };
            //       }) ?? [],
            // },
          ]}
        />
        <StatusFilter
          filterGroup={filterGroup}
          setFilterGroup={setFilterGroup}
        />
        <div className="relative">
          <TextInput
            className="h-8 w-72 pl-8"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search.."
            autoFocus
          />
          <div className="absolute inset-y-0 left-0 flex items-center pl-2.5">
            <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
          </div>
        </div>
      </div>
      <div className="flex flex-1 flex-col overflow-auto">
        {!!timeTrackingData ? (
          <TimeTrackingTable
            search={search}
            setSearch={setSearch}
            timeTrackingData={timeTrackingData}
          />
        ) : (
          <Loading className="mx-auto mt-[20vh]" />
        )}
      </div>
    </div>
  );
}

function TimeTrackingTable({
  search,
  setSearch,
  timeTrackingData,
}: {
  search: string;
  setSearch: (search: string) => void;
  timeTrackingData: ProjectTimeTrackingAnalyticsData[];
}) {
  const columns: ColumnDef<ProjectTimeTrackingAnalyticsData>[] = [
    {
      accessorKey: "name",
      header: "Project",
      cell: ({ row }) => {
        return <div className="truncate">{row.original.name}</div>;
      },
      // footer: () => {
      //   return <div>Total</div>;
      // },
    },
    {
      accessorKey: "project-progress",
      header: "Project Progress",
      cell: ({ row }) => {
        const totalTasks = row.original.totalTasks;
        const completedTasks = row.original.completedTasks;
        const projectProgress = (completedTasks / totalTasks) * 100;
        function getProjectProgress() {
          if (totalTasks === 0) {
            return <div className="w-32">-</div>;
          }
          return <div className="w-32">{projectProgress.toFixed(0) + "%"}</div>;
        }
        const completedStatusTasks =
          row.original.taskStatusBreakdown.find(
            (t) => t.parentStatus === "Completed"
          )?.taskCount ?? 0;
        const canceledStatusTasks =
          row.original.taskStatusBreakdown.find(
            (t) => t.parentStatus === "Canceled"
          )?.taskCount ?? 0;
        const stuckStatusTasks =
          row.original.taskStatusBreakdown.find(
            (t) => t.parentStatus === "Stuck"
          )?.taskCount ?? 0;
        const inProgressStatusTasks =
          row.original.taskStatusBreakdown.find(
            (t) => t.parentStatus === "In Progress"
          )?.taskCount ?? 0;
        const toDoActiveStatusTasks =
          row.original.taskStatusBreakdown.find(
            (t) => t.parentStatus === "To-do"
          )?.taskCount ?? 0;
        const backlogStatusTasks =
          row.original.taskStatusBreakdown.find(
            (t) => t.parentStatus === "Backlog"
          )?.taskCount ?? 0;
        // const inactiveStatusTasks =
        //   row.original.taskStatusBreakdown.find(
        //     (t) => t.parentStatus === "Inactive"
        //   )?.taskCount ?? 0;

        return (
          <HoverCard openDelay={50} closeDelay={10}>
            <HoverCardTrigger>{getProjectProgress()}</HoverCardTrigger>
            <HoverCardContent>
              <div className="space-y-2">
                <div className="flex items-center space-x-3 text-sm">
                  <div className="h-2 w-2 rounded-full bg-green-500" />
                  <div className="w-32 text-gray-700">Completed</div>
                  <div className="">
                    {!!completedStatusTasks ? completedStatusTasks : "-"}
                  </div>
                </div>
                <div className="flex items-center space-x-3 text-sm">
                  <div className="h-2 w-2 rounded-full bg-gray-300" />
                  <div className="w-32 text-gray-700">Canceled</div>
                  <div className="">
                    {!!canceledStatusTasks ? canceledStatusTasks : "-"}
                  </div>
                </div>
                <div className="flex items-center space-x-3 text-sm">
                  <div className="h-2 w-2 rounded-full bg-red-500" />
                  <div className="w-32 text-gray-700">Stuck</div>
                  <div className="">
                    {!!stuckStatusTasks ? stuckStatusTasks : "-"}
                  </div>
                </div>
                <div className="flex items-center space-x-3 text-sm">
                  <div className="h-2 w-2 rounded-full bg-blue-500" />
                  <div className="w-32 text-gray-700">In Progress</div>
                  <div className="">
                    {!!inProgressStatusTasks ? inProgressStatusTasks : "-"}
                  </div>
                </div>
                <div className="flex items-center space-x-3 text-sm">
                  <div className="h-2 w-2 rounded-full bg-amber-400" />
                  <div className="w-32 text-gray-700">To Do</div>
                  <div className="">
                    {!!toDoActiveStatusTasks ? toDoActiveStatusTasks : "-"}
                  </div>
                </div>
                <div className="flex items-center space-x-3 text-sm">
                  <div className="h-2 w-2 rounded-full bg-gray-200" />
                  <div className="w-32 text-gray-700">Backlog</div>
                  <div className="">
                    {!!backlogStatusTasks ? backlogStatusTasks : "-"}
                  </div>
                </div>
                {/* <div className="flex items-center space-x-3 text-sm">
                  <div className="h-2 w-2 rounded-full bg-gray-600" />
                  <div className="w-32 text-gray-700">Inactive</div>
                  <div className="">
                    {!!inactiveStatusTasks ? inactiveStatusTasks : "-"}
                  </div>
                </div> */}
              </div>
              <div className="my-2 border-y py-2">
                <div className="flex items-center space-x-3 text-sm">
                  <div className="w-2" />
                  <div className="w-32 text-gray-700">Total</div>
                  <div className="">{totalTasks}</div>
                </div>
              </div>
              <div className="flex items-center space-x-3 text-sm font-medium">
                <div className="w-2" />
                <div className="w-32 ">Closed %</div>
                <div className="">
                  {totalTasks
                    ? ((completedStatusTasks * 100) / totalTasks).toFixed(0) +
                      "%"
                    : "0" + "%"}
                </div>
              </div>
            </HoverCardContent>
          </HoverCard>
        );
      },
    },
    {
      accessorKey: "revenue",
      header: "Revenue",
      cell: ({ row }) => {
        return (
          <div className="w-32">
            {row.original.revenue
              ? "$" + formatCurrencyValue(row.original.revenue)
              : "-"}
          </div>
        );
      },
      // footer: ({ table }) => {
      //   const totalCapacity = table
      //     .getRowModel()
      //     .rows.reduce((acc, row) => acc + row.original.capacity, 0);
      //   return <div>{convertMsToNatural(totalCapacity)}</div>;
      // },
    },

    // {
    //   accessorKey: "allocatedTime",
    //   header: "Allocated Time",
    //   cell: ({ row }) => {
    //     return (
    //       <div className="w-32">
    //         {convertMsToNatural(row.original.allocatedTime)}
    //       </div>
    //     );
    //   },
    //   // footer: ({ table }) => {
    //   //   const totalAllocatedTime = table
    //   //     .getRowModel()
    //   //     .rows.reduce((acc, row) => acc + row.original.allocatedTime, 0);
    //   //   return <div>{convertMsToNatural(totalAllocatedTime)}</div>;
    //   // },
    // },
    // {
    //   accessorKey: "allocatedTimePercent",
    //   header: "Allocated Time %",
    //   cell: ({ row }) => {
    //     const allocatedTimePercent =
    //       (row.original.allocatedTime / row.original.capacity) * 100;
    //     return (
    //       <div
    //         className={clsx(
    //           "w-32"
    //           // allocatedTimePercent > 100 ? "bg-red-50" : ""
    //         )}
    //       >
    //         {allocatedTimePercent.toFixed(0) + "%"}
    //       </div>
    //     );
    //   },
    //   footer: ({ table }) => {
    //     const totalAllocatedTime = table
    //       .getRowModel()
    //       .rows.reduce((acc, row) => acc + row.original.allocatedTime, 0);
    //     const totalCapacity = table
    //       .getRowModel()
    //       .rows.reduce((acc, row) => acc + row.original.capacity, 0);
    //     return (
    //       <div>
    //         {((totalAllocatedTime / totalCapacity) * 100).toFixed(0) + "%"}
    //       </div>
    //     );
    //   },
    // },
    {
      accessorKey: "cost",
      header: "Cost",
      cell: ({ row }) => {
        return (
          <div className="w-32">
            {row.original.cost
              ? "$" + formatCurrencyValue(row.original.cost)
              : "-"}
          </div>
        );
      },
      // footer: ({ table }) => {
      //   const totalTrackedTime = table
      //     .getRowModel()
      //     .rows.reduce((acc, row) => acc + row.original.trackedTime, 0);
      //   return <div>{convertMsToNatural(totalTrackedTime)}</div>;
      // },
    },
    // {
    //   accessorKey: "trackedTimePercent",
    //   header: "Utilization",
    //   cell: ({ row }) => {
    //     return (
    //       <div className="w-32">
    //         {row.original.billingDetails?.budgetedTime ? (
    //           <>
    //             {(
    //               (row.original.trackedTime.total /
    //                 row.original.billingDetails.budgetedTime) *
    //               100
    //             ).toFixed(0) + "%"}
    //           </>
    //         ) : (
    //           "-"
    //         )}
    //       </div>
    //     );
    //   },
    //   // footer: ({ table }) => {
    //   //   const totalTrackedTime = table
    //   //     .getRowModel()
    //   //     .rows.reduce((acc, row) => acc + row.original.trackedTime, 0);
    //   //   const totalCapacity = table
    //   //     .getRowModel()
    //   //     .rows.reduce((acc, row) => acc + row.original.capacity, 0);
    //   //   return (
    //   //     <div>
    //   //       {((totalTrackedTime / totalCapacity) * 100).toFixed(0) + "%"}
    //   //     </div>
    //   //   );
    //   // },
    // },
    {
      accessorKey: "margin",
      header: "Margin",
      cell: ({ row }) => {
        const revenue = row.original.revenue;
        if (!revenue) {
          return <div className="w-32">-</div>;
        }
        const cost = row.original.cost;
        const margin = revenue - cost;
        return (
          <div
            className={clsx(
              "w-32",
              margin < 0 && "text-red-500",
              margin > 0 && "text-green-500"
            )}
          >
            {"$" + formatCurrencyValue(margin)}
          </div>
        );
      },
      // footer: ({ table }) => {
      //   const totalBillableTime = table
      //     .getRowModel()
      //     .rows.reduce((acc, row) => acc + row.original.billableTime, 0);
      //   return <div>{convertMsToNatural(totalBillableTime)}</div>;
      // },
    },
    {
      accessorKey: "marginPercent",
      header: "Margin %",
      cell: ({ row }) => {
        const revenue = row.original.revenue;
        if (!revenue) {
          return <div className="w-32">-</div>;
        }
        const cost = row.original.cost ?? 0;
        const margin = revenue - cost;
        const marginPercent = (margin / revenue) * 100;
        return (
          <div
            className={clsx(
              "w-32",
              marginPercent < 0 && "text-red-500",
              marginPercent > 0 && "text-green-500"
            )}
          >
            {marginPercent.toFixed(0) + "%"}
          </div>
        );
      },
    },

    // {
    //   id: "remainingTime",
    //   header: "Remaining Time",
    //   cell: ({ row }) => {
    //     if (
    //       !row.original.billingDetails ||
    //       !row.original.billingDetails.budgetedTime
    //     ) {
    //       return null;
    //     }
    //     const remainingTime =
    //       row.original.billingDetails.budgetedTime -
    //       row.original.trackedTime.billable;

    //     if (remainingTime <= 0) {
    //       return <div className="w-32">{"-"}</div>;
    //     }

    //     return <div className="w-32">{convertMsToNatural(remainingTime)}</div>;
    //   },
    // },
    // {
    //   id: "timeOverage",
    //   header: "Time Overage",
    //   cell: ({ row }) => {
    //     if (
    //       !row.original.billingDetails ||
    //       !row.original.billingDetails.budgetedTime
    //     ) {
    //       return null;
    //     }
    //     const remainingTime =
    //       row.original.billingDetails.budgetedTime -
    //       row.original.trackedTime.billable;

    //     if (remainingTime < 0) {
    //       return (
    //         <div className="w-32 text-red-500">
    //           {convertMsToNatural(Math.abs(remainingTime))}
    //         </div>
    //       );
    //     }

    //     return <div className="w-32">{"-"}</div>;
    //   },
    // },
    // {
    //   accessorKey: "billableTimePercent",
    //   header: "Billable Utilization",
    //   cell: ({ row }) => {
    //     return (
    //       <div className="w-32">
    //         {(
    //           (row.original.trackedTime.billable /
    //             row.original.trackedTime.total) *
    //           100
    //         ).toFixed(0) + "%"}
    //       </div>
    //     );
    //   },
    //   // footer: ({ table }) => {
    //   //   const totalBillableTime = table
    //   //     .getRowModel()
    //   //     .rows.reduce((acc, row) => acc + row.original.billableTime, 0);
    //   //   const totalCapacity = table
    //   //     .getRowModel()
    //   //     .rows.reduce((acc, row) => acc + row.original.capacity, 0);
    //   //   return (
    //   //     <div>
    //   //       {((totalBillableTime / totalCapacity) * 100).toFixed(0) + "%"}
    //   //     </div>
    //   //   );
    //   // },
    // },
  ];
  const [sorting, setSorting] = useState<SortingState>([]);

  const table = useReactTable({
    data: timeTrackingData ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    state: {
      sorting,
      globalFilter: search,
    },
    // columnVisibility: transformColumnsToDisplay(columnsToDisplay),
    globalFilterFn: fuzzyFilter,
    onSortingChange: setSorting,
    onGlobalFilterChange: setSearch,
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  // if (!utilizationData) {
  //   return <Loading className="mx-auto mt-[10vh]" />;
  // }

  // define periods based on aggregation and start/end date
  const navigate = useNavigate();
  return (
    <div className="flex-1 overflow-auto">
      <div className="max-h-full max-w-full overflow-auto">
        <table
          style={{ borderCollapse: "separate", borderSpacing: 0 }}
          className="min-w-[600px]"
        >
          <thead className="overflow-auto">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="border-b">
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    scope="col"
                    className={
                      "sticky top-0 z-10 h-11 whitespace-nowrap border-b border-r border-gray-200/70 bg-gray-50 px-6 text-left text-xs font-normal uppercase tracking-wide text-gray-500 first:left-0 first:z-20 first:pl-8 last:w-full"
                    }
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            <>
              <>
                {table.getRowModel().rows.map((row) => (
                  <tr
                    key={row.id}
                    className="group relative rounded-lg hover:cursor-pointer hover:bg-gray-50"
                    onClick={() => {
                      navigate(
                        `/time-tracking/entries?projectId=${row.original._id}`
                      );
                    }}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        className={
                          "whitespace-nowrap border-r border-b border-gray-100 px-6 py-3 text-sm text-gray-500 first:sticky first:left-0 first:z-10 first:w-full first:bg-white first:pr-6 first:font-medium first:text-gray-900 first:group-hover:bg-gray-50 first:sm:pl-8"
                        }
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </>
            </>
          </tbody>
          {/* {table.getRowModel().rows.length > 0 && (
            <tfoot className="">
              {table.getFooterGroups().map((footerGroup) => (
                <tr key={footerGroup.id}>
                  {footerGroup.headers.map((header, index) => (
                    <th
                      key={header.id}
                      className={clsx(
                        "sticky bottom-0 h-11 whitespace-nowrap border-r border-b border-gray-200/70 border-b-gray-200/70 bg-gray-50 px-6 text-left text-sm font-semibold text-gray-500 first:left-0 first:z-10 first:w-full first:pr-6 first:pl-8 first:font-medium first:text-gray-900 first:group-hover:bg-gray-50"
                        // index === 0 && "w-[240px] max-w-[240px]",
                        // index === 1 &&
                        //   "left-[240px] z-20 w-[160px] max-w-[160px] border-r-gray-200/70",
                        // index === 8 && "border-r-0",
                        // index === 9 &&
                        //   "sticky right-0 border-l border-l-gray-200/70 bg-gray-50",
                        // hoveredCell.columnId === header.column.id &&
                        //   hoveredCell.rowIndex !== -1 &&
                        //   hoveredCell.columnId !== "total"
                        //   ? "bg-gray-50 hover:bg-gray-100"
                        //   : "bg-gray-50"
                      )}
                    >
                      {flexRender(
                        header.column.columnDef.footer,
                        header.getContext()
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </tfoot>
          )} */}
        </table>
      </div>
    </div>
  );
}
